import React, { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import HomeDashboardLayout from '../homeDashboard/HomeDashboardLayout';
import MyWalletSidebar from '../homeDashboard/MyWalletSidebar';
import AppReviewsShimmer from './AppReviewsShimmer';
import PreferredLanguageToggle from '../components/PreferredLanguageToggle';
import { useAuth } from '../context/AuthContext';
import { fetchAppReviews, postAppReview } from '../services/appReviewService';
import './AppReviews.css';

export const clampRating = value => Math.min(5, Math.max(0, Number(value) || 0));

function Stars({ value }) {
  const rating = clampRating(value);
  return <span className="app-reviews-stars" role="img" aria-label={`${rating.toFixed(1)} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map(star => <i key={star} aria-hidden="true" className={rating >= star ? 'fas fa-star' : rating >= star - 0.5 ? 'fas fa-star-half-alt' : 'far fa-star'} />)}
  </span>;
}

function Avatar({ name, url }) {
  const [failed, setFailed] = useState(false);
  const valid = /^https?:\/\//i.test(String(url || ''));
  return <span className="app-reviews-avatar">{valid && !failed ? <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} /> : String(name || '?').slice(0, 1).toUpperCase()}</span>;
}

export default function AppReviews() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const language = String(user?.preferred_lang || user?.content_language || 'EN').toUpperCase();
  const hi = language === 'HI';
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(1);
  const [review, setReview] = useState('');
  const [notice, setNotice] = useState('');
  const submitting = useRef(false);
  const sentinel = useRef(null);
  const query = useInfiniteQuery({
    queryKey: ['appReviews', user?.id || user?.user_id, language],
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) => fetchAppReviews(pageParam, signal),
    getNextPageParam: (last, pages, lastPage) => {
      const current = Number(last.pagination?.currentPage) || lastPage;
      return current < Number(last.pagination?.totalPages || 1) ? current + 1 : undefined;
    },
    refetchOnMount: 'always',
    retry: false,
  });
  const { fetchNextPage, hasNextPage, isFetching, isFetchNextPageError } = query;
  useEffect(() => {
    if (!hasNextPage || isFetching || isFetchNextPageError || !window.IntersectionObserver || !sentinel.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) fetchNextPage({ cancelRefetch: false });
    }, { rootMargin: '150px' });
    observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetching, isFetchNextPageError]);

  const mutation = useMutation({ mutationFn: postAppReview });
  const submit = async event => {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    try {
      await mutation.mutateAsync({ rating, review });
      setOpen(false);
      setNotice(hi ? 'आपकी समीक्षा भेज दी गई है।' : 'Your review has been submitted.');
      // Remove previous pages so a successful post reloads page one only.
      client.setQueryData(['appReviews', user?.id || user?.user_id, language], old => old ? { pages: old.pages.slice(0, 1), pageParams: old.pageParams.slice(0, 1) } : old);
      await client.invalidateQueries({ queryKey: ['appReviews'] });
      client.invalidateQueries({ queryKey: ['sidebarData'] });
    } catch {
      // The mutation error is displayed in the form; keep the draft for retry.
    } finally { submitting.current = false; }
  };
  const summary = query.data?.pages[0];
  const rows = query.data?.pages.flatMap(page => page.reviews) || [];
  const close = () => { if (!submitting.current) setOpen(false); };

  return <HomeDashboardLayout showHeader={false} variant="app-reviews-layout">
    <main className="app-reviews-page">
      <header className="app-reviews-heading">
        <button type="button" className="app-reviews-back" aria-label="Go back" onClick={() => window.history.state?.idx > 0 ? navigate(-1) : navigate('/dashboard', { replace: true })}>‹</button>
        <PreferredLanguageToggle />
      </header>
      <div className="app-reviews-title"><h1>{hi ? 'समीक्षाएँ' : 'Reviews'}</h1><p>{hi ? 'विद्यार्थियों के अनुभव पढ़ें और अपनी राय साझा करें।' : 'Read student experiences and share your feedback.'}</p></div>
      {notice && <p role="status">{notice}</p>}
      {query.isPending ? <AppReviewsShimmer /> : query.isError && !summary ? <div role="alert"><p>{query.error?.message}</p><button type="button" onClick={() => query.refetch()}>Retry</button></div> : <div className="app-reviews-grid">
        <section className="app-reviews-summary" aria-label="Overall rating">
          <h2>{hi ? 'विद्यार्थियों की समीक्षाएँ' : 'Student Reviews'}</h2>
          <strong className="app-reviews-score">{clampRating(summary?.overall_rating).toFixed(1)}<small>/5</small></strong>
          <Stars value={summary?.overall_rating} />
          {String(summary?.rating_message || '').trim() && <p>{summary.rating_message}</p>}
          <button type="button" className="app-reviews-primary" onClick={() => { mutation.reset(); setRating(1); setReview(''); setOpen(true); }}>{hi ? 'समीक्षा लिखें' : 'Add a Review'}</button>
        </section>
        <section className="app-reviews-list" aria-label="Reviews">
          {!rows.length && <p>{hi ? 'अभी कोई समीक्षा नहीं है।' : 'No reviews yet. Be the first to share your feedback.'}</p>}
          {rows.map((row, index) => <article className="app-reviews-item" key={`${row.review_id ?? 'review'}-${index}`}>
            <div className="app-reviews-rating"><strong>{clampRating(row.rating).toFixed(1)}</strong><Stars value={row.rating} /></div>
            {String(row.review || '').trim() && <p>{row.review}</p>}
            <div className="app-reviews-user"><Avatar name={row.user_name} url={row.image_url} /><strong>{row.user_name || (hi ? 'विद्यार्थी' : 'Student')}</strong></div>
          </article>)}
          <div ref={sentinel} />
          {query.isFetchingNextPage && <AppReviewsShimmer more />}
          {query.isFetchNextPageError && <p role="alert">{query.error?.message}</p>}
          {query.hasNextPage && <button type="button" className="app-reviews-more" disabled={query.isFetching} onClick={() => query.fetchNextPage({ cancelRefetch: false })}>{query.isFetchingNextPage ? 'Loading…' : query.isFetchNextPageError ? 'Retry loading reviews' : 'Load more reviews'}</button>}
        </section>
      </div>}
    </main>
    <MyWalletSidebar withTopOffset={false} />
    <Modal show={open} onHide={close} centered backdrop={mutation.isPending ? 'static' : true} keyboard={!mutation.isPending} aria-labelledby="app-review-title">
      <form onSubmit={submit}>
        <Modal.Header closeButton={!mutation.isPending}><Modal.Title id="app-review-title">{hi ? 'समीक्षा लिखें' : 'Write a Review'}</Modal.Title></Modal.Header>
        <Modal.Body>
          <fieldset disabled={mutation.isPending} className="app-reviews-fieldset"><legend>{hi ? 'रेटिंग' : 'Score'}</legend>
            <div className="app-reviews-picker">{[1, 2, 3, 4, 5].map(star => <button type="button" key={star} aria-label={`${star} star${star > 1 ? 's' : ''}`} aria-pressed={rating === star} onClick={() => setRating(star)}><i aria-hidden="true" className={star <= rating ? 'fas fa-star' : 'far fa-star'} /></button>)}</div>
            <label htmlFor="app-review-text">{hi ? 'समीक्षा' : 'Review'}</label>
            <textarea id="app-review-text" rows={4} placeholder={hi ? 'अपनी समीक्षा लिखें' : 'Write your review'} value={review} onChange={event => setReview(event.target.value)} />
          </fieldset>
          {mutation.isError && <p role="alert" className="app-reviews-error">{mutation.error?.message}</p>}
        </Modal.Body>
        <Modal.Footer><button type="button" className="app-reviews-more" disabled={mutation.isPending} onClick={close}>{hi ? 'रद्द करें' : 'Cancel'}</button><button type="submit" className="app-reviews-primary" disabled={mutation.isPending}>{mutation.isPending ? 'Posting…' : hi ? 'पोस्ट करें' : 'Post'}</button></Modal.Footer>
      </form>
    </Modal>
  </HomeDashboardLayout>;
}
